/* eslint-disable @next/next/no-img-element */
import { CheckboxInput } from '@components/form/baseInputs'
import {
  AuthFill,
  Button,
  Comments,
  Date,
  DealerFinder,
  Email,
  FirstName,
  LastName,
  MarketingConsent,
  ModelGrid,
  Tel,
} from '@components/form/fields'
import { FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { useCampaign, useDealer } from '@contexts/index'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import {
  closeOverlay,
  getModelImage,
  getModels,
  getPageData,
} from '@utils/helpers'
import { formEvent } from '@utils/helpers/tracking'
import { TestDriveSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { upperCaseFirst } from 'upper-case-first'

import EVLogo from '../assets/icons/ev.svg'

const TestDrive = ({
  models,
  content,
  tracking,
  brand,
  dealer,
  model,
  query,
}) => {
  const { push, query: routerQuery } = useRouter()
  const clientDealer = useDealer()
  const campaign = useCampaign()
  const [formStep, setFormStep] = useState(1)
  const [isVehicleExpanded, expandVehicle] = useState(false)
  const [isDealerExpanded, expandDealer] = useState(false)
  const [isDetailsExpanded, expandDetails] = useState(false)
  const [formData, setFormData] = useState({})
  const [submitResponse, setSubmitResponse] = useState('')
  const [user, setUser] = useState(false)
  const [selectedDealer, setSelectedDealer] = useState({})

  if (clientDealer) {
    // eslint-disable-next-line no-param-reassign
    dealer = clientDealer
  }

  const step = (newFormStep, userState) => {
    setUser(userState)
    setFormStep(newFormStep)
  }

  const configID = (query?.configId || query?.code)?.replace(
    /[^a-zA-Z0-9]/g,
    '',
  )

  const initialValues = {
    first_name: '',
    last_name: '',
    email: '',
    tel: '',
    model: '',
    date: '',
    time: 'AM',
    notes: '',
    testdrive_type: 'Standard',
    consent: {
      user: false,
    },
    response_code: '',
    campaign_response_code: '',
    campaign_code: '',
    crm_response_source: '',
    is_dealer: query?.source === 'dealer' || query?.source === 'centre',
    dealer: 0,
    dealer_name: '',
    configID: configID ? `tme.one/${configID}` : '',
    is_lexus: brand === 'lexus',
    enhanced_testdrive: false,
    enhanced_vehicle: {
      id: '',
      label: '',
    },
  }

  const methods = useForm({
    resolver: yupResolver(TestDriveSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  const handleSetDealer = (dealerObj) => {
    methods.setValue('dealer', dealerObj.id)
    methods.setValue('dealer_name', dealerObj.name)
    step(3, true)
    setSelectedDealer(dealerObj)
  }

  const isEnhancedTestDriveSelected = methods.watch('enhanced_testdrive')

  useEffect(() => {
    let params = routerQuery
    if (isEnhancedTestDriveSelected) {
      params = {
        ...routerQuery,
        ev_enhance: methods.getValues('enhanced_vehicle.id'),
      }
    } else {
      delete params.ev_enhance
    }

    push({ query: { ...params } }, undefined, {
      shallow: true,
    })
  }, [isEnhancedTestDriveSelected])

  useEffect(() => {
    if (query?.type?.toLowerCase() === '48hr') {
      methods.setValue('testdrive_type', 'FortyEightHr')
    }
    if (query?.featured?.toLowerCase() === 'motability') {
      methods.setValue('testdrive_type', 'Motability TD')
    }
    if (query?.featured?.toLowerCase() === 'fleet') {
      methods.setValue('testdrive_type', 'Fleet TD')
    }
  }, [query])

  useEffect(() => {
    if (campaign?.campaign) {
      methods.setValue('campaign_code', campaign.campaign)
    }

    if (campaign?.media) {
      const code = methods.getValues('campaign_code')
      methods.setValue('campaign_code', code + campaign.media)
    }

    if (campaign?.testDrive) {
      methods.setValue('campaign_response_code', campaign.testDrive)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign])

  useEffect(() => {
    if (model) {
      if (brand === 'lexus') {
        methods.setValue('response_code', model?.responsecodes?.test_drive)
      }
      methods.setValue('model', model.d4t4.id)
      setFormData((state) => {
        return {
          ...state,
          modelImage: getModelImage(
            brand,
            model?.image?.id,
            model?.image?.grade,
            model?.image?.color,
            'webp',
            model?.image?.fallback,
          ),
          modelName: model.name,
          fuel: model?.filters?.fuel[0],
        }
      })
      step(dealer ? 3 : 2, false)
    }
  }, [model])

  useEffect(() => {
    if (dealer) {
      methods.setValue('is_dealer', true)
      methods.setValue('testdrive_type', 'TwentyFourHr')
      methods.setValue(
        'dealer',
        dealer?.applications?.NMSC?.externalID
          ? dealer.applications.NMSC.externalID
          : '',
      )
      methods.setValue('dealer_name', dealer.name)
      setSelectedDealer(dealer)
      step(model ? 3 : 1, false)
    }
  }, [dealer])

  const VehicleStep = () =>
    formData.modelName ? (
      <div
        className={`relative ${brand === 'lexus' ? ' uppercase' : ''}`}
        data-gt-name="componentevent"
        data-gt-componentname="test-drive"
      >
        <div className="md:flex items-center">
          <button
            className="flex hover:underline"
            onClick={() => {
              step(1, true)
              expandDetails(false)
              expandVehicle(false)
              methods.setValue('dealer', '')
              methods.setValue('dealer_name', '')
              setSelectedDealer({})
              setFormData((state) => {
                return {
                  ...state,
                  modelImage: '',
                  modelName: '',
                  fuel: null,
                  dealerID: '',
                  dealerName: '',
                  dealerDistance: '',
                  address: '',
                }
              })
              setSubmitResponse('')
            }}
            // data-gt-componentgroup="forms"
            // data-gt-componentname="test-drive"
            // data-gt-name="navevent"
            // data-gt-action="nav-click-cta"
            // data-gt-label="change vehicle"
          >
            <div className="flex flex-col items-baseline md:flex-row md:space-x-2 md:justify-between">
              <div
                className={`font-medium ${
                  brand === 'lexus' ? ' uppercase' : ''
                }`}
              >
                1. {brand === 'lexus' ? 'Car' : 'Vehicle'}
              </div>
              <span className="text-tiny">Change</span>
            </div>
          </button>
        </div>
        <button
          className="md:flex text-tiny hidden mt-1"
          data-gt-action={`nav-${
            isVehicleExpanded ? 'open' : 'close'
          }-dropdown`}
          data-gt-label="vehicle"
          onClick={() => {
            expandVehicle((state) => !state)
            expandDealer(false)
            expandDetails(false)
          }}
        >
          <div>{formData.modelName}</div>
          {formData.label && <div className="ml-1"> {formData.label}</div>}

          <div className="ml-1 mt-1">
            {isVehicleExpanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={3}
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 15.75l7.5-7.5 7.5 7.5"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={3}
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            )}
          </div>
        </button>
        {isVehicleExpanded && (
          <div
            className="absolute top-[70px] p-[15px] shadow-md z-10 w-[300px] bg-white"
            data-gt-componentgroup="forms"
            data-gt-name="componentevent"
            data-gt-componentname="test-drive"
          >
            <div className="flex justify-between">
              <div>
                <div className="text-lg font-medium">{formData.modelName}</div>
                {formData.fuel !== undefined &&
                  formData.fuel.toLowerCase() !== 'petrol' &&
                  formData.fuel.toLowerCase() !== 'diesel' && (
                    <div
                      className={`${
                        brand === 'toyota' ? 'font-medium text-blue' : ''
                      } uppercase`}
                    >
                      {formData.fuel}
                    </div>
                  )}
              </div>

              <button
                onClick={() => {
                  expandVehicle((state) => !state)
                  expandDealer(false)
                  expandDetails(false)
                }}
                data-gt-action="nav-close-dropdown"
                data-gt-label="vehicle"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <img
              className="w-full font-medium mt-4"
              src={formData.modelImage}
              alt={formData.modelName}
            />
          </div>
        )}
      </div>
    ) : (
      <div className="font-semibold">
        1. {brand === 'lexus' ? 'Car' : 'Vehicle'}
      </div>
    )

  const DealerStep = () =>
    selectedDealer.name ? (
      <div
        className={`relative ${brand === 'lexus' ? ' uppercase' : ''}`}
        data-gt-name="componentevent"
        data-gt-componentname="test-drive"
      >
        <div className="md:flex items-center">
          <button
            className="flex hover:underline"
            onClick={() => {
              step(2, true)
              expandDealer(false)
              expandDetails(false)
              expandVehicle(false)
              methods.setValue('dealer', '')
              methods.setValue('dealer_name', '')
              setSelectedDealer({})
            }}
            // data-gt-componentgroup="forms"
            // data-gt-componentname="test-drive"
            // data-gt-name="navevent"
            // data-gt-action="nav-click-cta"
            // data-gt-label="change dealer"
          >
            <div className="flex flex-col items-baseline md:flex-row md:space-x-2 md:justify-between">
              <div
                className={`font-medium ${
                  brand === 'lexus' ? ' uppercase' : ''
                }`}
              >
                2. {brand === 'lexus' ? 'Centre' : 'Dealer'}
              </div>
              <span className="text-tiny">Change</span>
            </div>
          </button>
        </div>
        <button
          className="md:flex text-tiny hidden mt-1"
          onClick={() => {
            expandDealer((state) => !state)
            expandVehicle(false)
            expandDetails(false)
          }}
          data-gt-action={`nav-${isDealerExpanded ? 'open' : 'close'}-dropdown`}
          data-gt-label="dealer"
        >
          <div className="break-all w-full"> {selectedDealer?.name} </div>

          <div className="ml-1 mt-1">
            {isDealerExpanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={3}
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 15.75l7.5-7.5 7.5 7.5"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={3}
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            )}
          </div>
        </button>
        {isDealerExpanded && (
          <div
            className="absolute top-[70px] p-[15px] shadow-md z-10 w-[300px] bg-white"
            data-gt-componentgroup="forms"
            data-gt-name="componentevent"
            data-gt-componentname="test-drive"
          >
            <div className="flex justify-between">
              <div className="flex">
                <div className="text-tiny lowercase">
                  {selectedDealer.distance &&
                    `${(selectedDealer.distance * 0.62137119).toFixed(
                      2,
                    )} miles`}
                </div>
              </div>

              <button
                onClick={() => {
                  expandDealer((state) => !state)
                  expandVehicle(false)
                  expandDetails(false)
                }}
                data-gt-action="nav-close-dropdown"
                data-gt-label="dealer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {selectedDealer?.name && (
              <div className="my-[10px] font-medium">
                {selectedDealer?.name}
              </div>
            )}

            {selectedDealer?.address?.address && (
              <div className="normal-case">
                {' '}
                {selectedDealer?.address?.address}
              </div>
            )}
            {selectedDealer?.address?.address1 && (
              <div className="normal-case">
                {' '}
                {selectedDealer?.address?.address1}
              </div>
            )}
            {selectedDealer?.address?.city && (
              <div className="normal-case">
                {' '}
                {selectedDealer?.address?.city}
              </div>
            )}
            {selectedDealer?.address?.zip && (
              <div className="normal-case"> {selectedDealer?.address?.zip}</div>
            )}
          </div>
        )}
      </div>
    ) : (
      <div className="flex flex-row">
        <div className={`${formStep === 2 ? 'font-semibold' : 'font-medium'}`}>
          2. {brand === 'lexus' ? 'Centre' : 'Dealer'}
        </div>
      </div>
    )

  const DetailsStep = () =>
    formData.first_name ? (
      <div className={`relative ${brand === 'lexus' ? ' uppercase' : ''}`}>
        <div className="md:flex items-center">
          <button
            className="flex hover:underline"
            onClick={() => {
              step(4, true)
              expandDealer(false)
              expandDetails(false)
              expandVehicle(false)
              setFormData((state) => {
                return {
                  ...state,
                  first_name: '',
                  last_name: '',
                  email: '',
                  phone: '',
                }
              })
            }}
            // data-gt-componentgroup="forms"
            // data-gt-componentname="test-drive"
            // data-gt-name="navevent"
            // data-gt-action="nav-click-cta"
            // data-gt-label="change details"
          >
            <div className="flex flex-col items-baseline md:flex-row md:space-x-2 md:justify-between">
              <div
                className={`font-medium ${
                  brand === 'lexus' ? ' uppercase' : ''
                }`}
              >
                3. Details
              </div>
              <span className="text-tiny">Change</span>
            </div>
          </button>
        </div>
        <button
          className="md:flex text-[13px] mt-[10px] leading-[10px] invisible lg:visible"
          onClick={() => {
            expandDetails((state) => !state)
            expandDealer(false)
            expandVehicle(false)
          }}
          data-gt-action={`nav-${isDealerExpanded ? 'open' : 'close'}-dropdown`}
          data-gt-label="details"
        >
          <div>{`${formData.first_name} `} </div>

          <div className="ml-1 mt-1">
            {isDetailsExpanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={3}
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 15.75l7.5-7.5 7.5 7.5"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={3}
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            )}
          </div>
        </button>
        {isDetailsExpanded && (
          <div
            className="absolute top-[70px] p-[15px] shadow-md z-10 w-[300px] bg-white"
            data-gt-componentgroup="forms"
            data-gt-name="componentevent"
            data-gt-componentname="test-drive"
          >
            <div className="flex justify-between  ">
              <div className="">
                {`${formData.first_name} ${formData.last_name}`}
              </div>

              <button
                onClick={() => {
                  expandDetails((state) => !state)
                  expandDealer(false)
                  expandVehicle(false)
                }}
                data-gt-action="nav-close-dropdown"
                data-gt-label="details"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className=""> {`${formData.email} `}</div>
            <div className=""> {`${formData.phone} `}</div>
          </div>
        )}
      </div>
    ) : (
      <div className="flex flex-row">
        <div className={`${formStep === 3 ? 'font-semibold' : 'font-medium'}`}>
          3. Details
        </div>
      </div>
    )

  const onSubmit = async (data) => {
    setSubmitResponse('')
    const formatData = data

    if (data.enhanced_testdrive) {
      formatData.notes = `Customer would like to enhance their visit and test drive the ${data.enhanced_vehicle.label}`
    }

    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/test-drive`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: formatData,
    })
      .then(() => {
        formEvent({
          conversion: 1,
          name: tracking.form,
          step: 'form-submit',
          variant: '',
          page: tracking.page,
        })
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent({
          conversion: 0,
          name: tracking.form,
          step: 'form-error',
          variant: '',
          page: tracking.page,
        })
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav
        title={content.title}
        steps={[VehicleStep(), DealerStep(), DetailsStep()]}
      />
      <FormWrapper>
        <FormStep
          title={content.car.title}
          subtitle="<p> If you're a fleet manager or small business owner, <a href='/fleet/callback'  rel='noopener noreferrer' data-gt-name='link' data-gt-action=`callback-click-link` data-gt-label='callback-link' target='_blank'>click here</a> to request a demo.</p>"
          step={formStep}
          active={formStep === 1}
          user={user}
          tracking={{
            conversion: 0,
            name: tracking.form,
            step: 'select-model',
            variant: tracking.variant,
            page: tracking.page,
          }}
        >
          <ModelGrid
            models={models}
            showFilter={content.car.filter}
            setModel={(selectedModel) => {
              if (brand === 'lexus') {
                methods.setValue(
                  'response_code',
                  selectedModel?.responsecodes?.test_drive,
                )
              }

              methods.resetField('enhanced_testdrive')

              if (
                brand === 'toyota' &&
                selectedModel.d4t4.id === 'All New RAV4 Hybrid'
              ) {
                methods.setValue('enhanced_vehicle', {
                  id: 'rav4PluginHybrid',
                  label: 'RAV4 Plug-in Hybrid',
                })
              }

              if (brand === 'toyota' && selectedModel.d4t4.id === 'RAV4 PHEV') {
                methods.setValue('enhanced_vehicle', {
                  id: 'bx4x',
                  label: 'All-Electric bZ4X',
                })
              }

              if (brand === 'lexus' && selectedModel.d4t4.id.includes('NX')) {
                methods.setValue('enhanced_vehicle', {
                  id: 'rz',
                  label: 'All-Electric RZ',
                })
              }

              if (brand === 'lexus' && selectedModel.d4t4.id.includes('RX')) {
                methods.setValue('enhanced_vehicle', {
                  id: 'rz',
                  label: 'All-Electric RZ',
                })
              }

              methods.setValue('model', selectedModel.d4t4.id)
              setTimeout(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
              }, 100)
              if (methods.getValues('dealer')) {
                step(3, true)
              } else {
                step(2, true)
              }

              setFormData((state) => {
                return {
                  ...state,
                  modelImage: getModelImage(
                    brand,
                    selectedModel?.image?.id,
                    selectedModel?.image?.grade,
                    selectedModel?.image?.color,
                    'webp',
                    selectedModel?.image?.fallback,
                  ),
                  modelName: selectedModel.name,
                  fuel: selectedModel?.filters?.fuel[0] || undefined,
                }
              })
            }}
          />
        </FormStep>
      </FormWrapper>

      <FormStep
        step={formStep}
        active={formStep === 2}
        user={user}
        noContainer={true}
        tracking={{
          conversion: 0,
          name: tracking.form,
          step: 'select-dealer-get-location',
          variant: tracking.variant,
          page: tracking.page,
        }}
      >
        <div className="relative z-[1]">
          <DealerFinder setDealer={handleSetDealer} />
        </div>
      </FormStep>

      <FormWrapper>
        <FormStep
          title={content.details.title}
          step={formStep}
          active={formStep === 3}
          user={user}
          tracking={{
            conversion: 0,
            name: tracking.form,
            step: 'contact-details',
            variant: tracking.variant,
            page: tracking.page,
          }}
        >
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              <FirstName />
              <LastName />
              <Email />
              <Tel
                optional
                helper="Leave your phone number if you would like to discuss your test drive over the phone."
              />
              {brand === 'lexus' && (
                <Date
                  showAmPm={true}
                  helper="Let us know if you have a preferred date or time and we'll do our best to arrange your booking for this time."
                />
              )}
              <Comments
                label="Additional Comments"
                helper="Let us know if you have any specific requirements."
              />

              {methods.getValues('enhanced_vehicle.id') && (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 md:gap-4 items-start justify-top mt-8 mb-4 md:mb-2">
                  <div className="pt-5 pb-6 px-6 border-2 border-blue bg-primary md:col-span-2 lg:col-span-1">
                    <div className="flex flex-col-reverse md:flex-row items-end md:items-center gap-4 mb-4">
                      <h4 className="font-semibold">
                        Would you like to enhance your visit and test drive the{' '}
                        {methods.getValues('enhanced_vehicle.label')}?
                      </h4>
                      <EVLogo className={'text-blue shrink-0'} />
                    </div>
                    <CheckboxInput
                      name={'enhanced_testdrive'}
                      label={`Yes, I would like to test drive the ${methods.getValues(
                        'enhanced_vehicle.label',
                      )}`}
                    />
                  </div>
                </div>
              )}
              <MarketingConsent />

              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'test-drive'
  const content = await import(`../content/${brand}/forms/${form}.md`).catch(
    () => null,
  )
  const settings = await import(`../content/${brand}/settings/global.md`).catch(
    () => null,
  )

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      models: getModels(brand, form),
      content: content.attributes,
      settings: settings.attributes,
      title: content.attributes.title,
      tracking: {
        page: 'test-drive',
        form: 'lead-test-drive',
        variant: 'test-drive',
        initialStep: 'select-model',
      },
      description: `A test drive is the best way to check which ${upperCaseFirst(
        brand,
      )} is the one for you and booking is easy. Interested? Get in touch directly with one of our ${upperCaseFirst(
        brand,
      )} experts.`,
      ...(await getPageData(context, form)),
    },
  }
}

TestDrive.whyDidYouRender = true

export default TestDrive
